import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sun, Plane, Clock, FileCheck2, ArrowRight, CalendarDays, ThermometerSun } from "lucide-react";
import api from "../../lib/api";

/* TODAY — in-trip command center: where am I, what happens next, what do I need to do.
   Renders only while a trip is underway or starting within 2 days. */

const MODE_LABEL = { flight: "Flight", train: "Train", bus: "Bus", ferry: "Ferry", taxi: "Taxi",
  car: "Drive", hotel: "Hotel", pickup: "Pickup", reservation: "Reservation", other: "Leg" };

export default function TodayCard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/trips/today").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data?.trip) return null;
  const { trip, starts_in_days, today_segments, next_segment, leave_by, docs, weather } = data;
  const seg = (s, i) => (
    <div key={i} className="flex items-center gap-2 text-sm" data-testid={`today-seg-${i}`}>
      <Plane className="w-4 h-4 shrink-0 opacity-80" />
      {s.time && <span className="font-mono font-bold">{s.time.slice(0, 5)}</span>}
      {s.ref && <span className="font-mono font-bold">{s.ref}</span>}
      <span className="truncate">{MODE_LABEL[s.mode] || "Leg"} · {s.from_place}{s.to_place ? ` → ${s.to_place}` : ""}</span>
    </div>
  );
  return (
    <div className="border-2 border-amber-500 bg-gradient-to-r from-amber-50 to-white mb-8" data-testid="today-card">
      <div className="bg-amber-500 text-white px-4 py-1.5 flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-widest">
        <Sun className="w-4 h-4" />
        {starts_in_days > 0 ? `Trip starts in ${starts_in_days} day${starts_in_days > 1 ? "s" : ""}` : "Today"}
        <span className="opacity-80">· {trip.title}</span>
      </div>
      <div className="p-4 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-2 min-w-0">
          {today_segments.length > 0 ? today_segments.slice(0, 4).map(seg)
            : next_segment ? (
              <div className="text-sm text-zinc-700" data-testid="today-next">
                <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-amber-700">Next up · {next_segment.date}</span>
                {seg(next_segment, "next")}
              </div>
            ) : <p className="text-sm text-muted-foreground">No legs scheduled today — enjoy the day!</p>}
          {leave_by && (
            <div className="inline-flex items-center gap-1.5 text-xs font-700 bg-rose-50 border border-rose-300 text-rose-700 px-2 py-1" data-testid="today-leave-by">
              <Clock className="w-3.5 h-3.5" /> Suggested: leave for the airport by {leave_by} (3h buffer — a recommendation, not a guarantee)
            </div>
          )}
        </div>
        <div className="space-y-1.5 text-sm">
          {weather && (
            <div className="flex items-center gap-1.5" data-testid="today-weather">
              <ThermometerSun className="w-4 h-4 text-amber-600" />
              <span>{weather.place}: {weather.hi != null ? `${Math.round(weather.hi)}°` : ""}{weather.lo != null ? ` / ${Math.round(weather.lo)}°` : ""}{weather.rain_pct != null && weather.rain_pct >= 30 ? ` · ${weather.rain_pct}% rain` : ""}</span>
            </div>
          )}
          {docs.total > 0 && (
            <div className="flex items-center gap-1.5" data-testid="today-docs">
              <FileCheck2 className={`w-4 h-4 ${docs.done === docs.total ? "text-emerald-600" : "text-amber-600"}`} />
              <span>Documents: {docs.done}/{docs.total} ready</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <CalendarDays className="w-4 h-4" /> {trip.start_date} → {trip.end_date}
          </div>
          <Link to={`/trips/${trip.id}`} data-testid="today-open-trip"
            className="inline-flex items-center gap-1 text-sm font-700 text-amber-700 hover:underline">
            Open trip timeline <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
